import { MultiSelect } from '@lmc/ui-kit'
import { Form } from 'antd'
import { Text } from '@lmc/ui-kit/src/general'
import { useRef, useEffect, useState } from 'react'
import { showToast } from 'common/utils'

// Wrapper component to adapt Form.Item's value/onChange to MultiSelect's values/onChange
export const TeamMembersMultiSelect = ({
  value,
  onChange,
  title,
  groupedOptions,
  formData,
  teamMembersCount,
  maxTeamMembers,
  validator,
}: {
  value?: any[]
  onChange?: (values: any[]) => void
  title: string
  groupedOptions: any
  formData?: Record<string, any>
  teamMembersCount: number
  maxTeamMembers: number
  validator?: (rule: any, value: any) => Promise<void>
}) => {
  const form = Form.useFormInstance()
  const previousValueRef = useRef(value || formData?.teamMembers || [])
  const isValidationPendingRef = useRef(false)
  const isRevertingRef = useRef(false)
  const [revertKey, setRevertKey] = useState(0)

  // Update ref when value changes (validation passed)
  useEffect(() => {
    const currentValue = value || formData?.teamMembers || []
    if (!isValidationPendingRef.current && !isRevertingRef.current) {
      previousValueRef.current = currentValue
    }
    // Reset reverting flag after value updates
    if (isRevertingRef.current) {
      isRevertingRef.current = false
    }
  }, [value, formData])

  const displayValue = value || formData?.teamMembers || []
  // Use a combination of value and revert key to force re-render on revert
  const componentKey = `${JSON.stringify(displayValue)}-${revertKey}`

  return (
    <MultiSelect
      key={componentKey} // Force re-render when value changes/reverts
      title={title}
      groupedOptions={groupedOptions}
      hasClear
      hasSearch
      hasSelectAll
      placeholder
      maxTagCount={null}
      values={displayValue}
      onChange={async (newValues) => {
        // Skip validation if we're reverting
        if (isRevertingRef.current) {
          return
        }

        // MultiSelect always passes IDs (strings) in newValues, not objects
        // But the form might store them as objects, so we need to check the format of displayValue
        const isObjectFormat = Array.isArray(displayValue) && displayValue.length > 0 && typeof displayValue[0] === 'object'
        
        // Normalize newValues to ensure consistent format (array of IDs/values)
        const normalizedNewValues = Array.isArray(newValues)
          ? newValues.map((member: any) =>
              typeof member === 'object' ? member.value : member
            )
          : []

        // Normalize previousValue to ensure consistent format
        const previousValue = previousValueRef.current
        const normalizedPreviousValue = Array.isArray(previousValue)
          ? previousValue.map((member: any) =>
              typeof member === 'object' ? member.value : member
            )
          : []
        
        // Validate before updating
        if (validator) {
          isValidationPendingRef.current = true
          try {
            // Create a mock rule object for the validator
            const mockRule = {} as any
            await validator(mockRule, normalizedNewValues)
            // Validation passed - update the value
            // Store in the same format as displayValue
            previousValueRef.current = isObjectFormat 
              ? newValues.map((id: string) => {
                  // First, try to find the object from previousValue to preserve format
                  const obj = previousValue.find((m: any) => {
                    const mId = typeof m === 'object' ? m.value : m
                    return mId === id
                  })
                  if (obj) {
                    return obj
                  }
                  // If not found in previousValue (newly added), look it up in groupedOptions
                  if (groupedOptions) {
                    for (const group of groupedOptions) {
                      const option = group.options?.find((opt: any) => opt.value === id)
                      if (option) {
                        return { value: option.value, label: option.label }
                      }
                    }
                  }
                  // Fallback: create object with ID as label
                  return { value: id, label: id }
                })
              : newValues
            onChange?.(previousValueRef.current)
          } catch (error) {
            // Validation failed - get the specific clocked-in team member IDs from the error
            const clockedInIds = (error as any)?.clockedInIds || []
            
            // Only add back the team members that are actually clocked in
            // This ensures we only restore the ones that shouldn't be removed, not all removed members
            const restoredIds = [...normalizedNewValues, ...clockedInIds]
            
            // Convert back to the format that the form expects (same as displayValue)
            const formatToRestore = isObjectFormat
              ? restoredIds.map((id: string) => {
                  // Try to find the original object from previousValue to preserve label/name
                  const originalObj = previousValue.find((m: any) => {
                    const mId = typeof m === 'object' ? m.value : m
                    return mId === id
                  })
                  // If we found the original object, use it to preserve the label
                  if (originalObj) {
                    return originalObj
                  }
                  // If not found, try to find it in groupedOptions to get the label
                  if (groupedOptions) {
                    for (const group of groupedOptions) {
                      const option = group.options?.find((opt: any) => opt.value === id)
                      if (option) {
                        return { value: option.value, label: option.label }
                      }
                    }
                  }
                  // Fallback: create object with ID as label
                  return { value: id, label: id }
                })
              : restoredIds

            const errorMessage = error instanceof Error ? error.message : 'Some team members are already clocked in'
            // Show error message to user
            showToast({
              type: 'warning',
              title: errorMessage,
            })
            // Set reverting flag to skip validation on revert
            isRevertingRef.current = true
            // Clear any validation errors for this field
            form.setFields([{ name: 'teamMembers', errors: [] }])
            // Restore only the team members that are clocked in
            previousValueRef.current = formatToRestore
            onChange?.(formatToRestore)
            // Wait a tick for form to update, then increment revert key to force MultiSelect re-render
            setTimeout(() => {
              setRevertKey((prev) => prev + 1)
              isRevertingRef.current = false
            }, 0)
          } finally {
            isValidationPendingRef.current = false
          }
        } else {
          // No validator - just update
          onChange?.(newValues)
        }
      }}
      suffixIcon={
        <Text>
          {teamMembersCount}/{maxTeamMembers}
        </Text>
      }
    />
  )
}

